import { graphql } from 'gatsby';
import React, { useState } from 'react';
import BodyClassName from 'react-body-classname';

import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';

import { formatTitle } from '../../utils/shop';
import { getSectionTitleFromHTML, titleCase } from '../../utils/title-case';
import { getSliceNumber } from '../../utils/podCast';
import SocialShare from '../../components/Shop/SocialShare';
import ReadPageContent from '../../components/Read/ReadPageContent';
import ReadAboutAuthor from '../../components/Read/ReadAboutAuthor';
import ChapterActions from '../../components/Read/ChapterActions';
import RelatedContent from '../../components/podcast/RelatedContent';
import DefaultLayoutComponent from '../layouts/DefaultLayoutComponent';
import { formatReadDuration } from '../../utils/functions';
import { resolveArticleAuthorMember, resolveReadBylineAuthor } from '../../utils/readBylineAuthor';
import {
  extractReadArticleGalleryUrls,
  getReadAuthorGallerySliceId,
} from '../../utils/readArticleGallery';
import { getGcUi } from '../../constants/spanish-gc';
import Stickysidebar from '../../components/Read/Stickysidebar';
import ArrowLeft from '../../images/read-preview/left.png';
import ArrowRight from '../../images/read-preview/right.png';

const isListEmpty = (arr) => {
  if (Array.isArray(arr) && !!arr?.length) {
    return false;
  }

  return true;
};

export function ReadSingleRender({
  read,
  currentArticleUid,
  podCastData,
  readData,
  totalWatchData,
  teamMembers = [],
}) {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [, setShowReadModal] = useState(false);

  if (!read) {
    console.error('read undefined in read-page');

    return null;
  }

  var seoData = {
    canonical: read?.uri,
    title: titleCase(read?.title?.text),
    metaDesc: read?.metaDescriptions?.text || `Read more about ${titleCase(read?.title.text)}`,
    opengraphDescription: read?.metaDescriptions?.text || `Read more about ${titleCase(read?.title.text)}`,
    twitterDescription: read?.metaDescriptions?.text || `Read more about ${titleCase(read?.title.text)}`,
    opengraphTitle: titleCase(read?.title.text) || '',
    twitterTitle: titleCase(read?.title.text) || '',
    opengraphImage: {
      sourceUrl: read?.featuredImage?.node?.sourceUrl,
    },
    twitterImage: {
      sourceUrl: read?.featuredImage?.node?.sourceUrl,
    },
    opengraphUrl: read?.uri || '',
  };

  const {
    title,
    readDuration,
    postBanner,
    furtherReading,
    furtherListening,
    footnotes,
    featuredVideo,
    featuredVideoThumbnail,
    readCategories,
    featuredPodcast,
    bylineAuthor,
    articleAuthor,
  } = read;

  const defaultBylineAuthor = getGcUi('en').chapterSummaryAuthor;
  const readBylineAuthorName = resolveReadBylineAuthor(
    { bylineAuthor, articleAuthorLink: articleAuthor, teamMembers },
    defaultBylineAuthor
  );
  const articleAuthorMember = resolveArticleAuthorMember(
    articleAuthor,
    teamMembers,
    readBylineAuthorName,
    defaultBylineAuthor
  );
  const authorGallerySliceId = getReadAuthorGallerySliceId(read?.body1);
  const readArticleGalleryUrls = extractReadArticleGalleryUrls(read?.body1);
  const relocateAuthorGallerySliceId =
    articleAuthorMember && authorGallerySliceId ? authorGallerySliceId : null;
  const relatedListen = read.relatedListen
    ?.filter((content) => content?.relatedListen?.uid)
    .map((item) => {
      const pod = podCastData.nodes.find((pod) => pod.uid === item.relatedListen.uid);
      return {
        ...pod,
        category: 'LISTEN',
        url: `/podcast/${pod.uid}`,
      };
    });

  const relatedEpisodes = read.relatedEpisodes
    ?.filter((content) => content?.episode?.uid)
    .map((item) => {
      const watch = totalWatchData.nodes.find((watch) => watch.uid === item.episode.uid);

      return {
        ...watch,
        category: 'WATCH',
        uid: item.episode.uid,
      };
    });

  const relatedRead = read.relatedRead
    ?.filter((content) => content?.read?.uid)
    .map((item) => {
      const read = readData.nodes.find((read) => read.uid === item?.read?.uid);
      return {
        ...read.data,
        category: 'READ',
        url: read.url,
      };
    });

  const relatedShop = read.relatedProducts
    .filter((item) => !!item.product)
    .map((p) => {
      return {
        thumbnailUrl: p.product.image.src,
        category: 'SHOP',
        title: p.product.title,
        url: `/product/${formatTitle(p.product.title)}`,
      };
    });

  const relatedReadCount = relatedRead?.length;
  const relatedEpisodesCount = relatedEpisodes?.length;
  const relatedShopCount = relatedShop?.length;
  const relatedListenCount = relatedListen?.length;

  let relatedData = [];

  if (relatedEpisodes.length) {
    const sliceNumber = getSliceNumber(relatedEpisodesCount, relatedShopCount, relatedListenCount, relatedData);
    relatedData = relatedData.concat(relatedEpisodes.slice(0, sliceNumber));
  }
  if (relatedRead.length) {
    const sliceNumber = getSliceNumber(relatedEpisodesCount, relatedShopCount, relatedListenCount, relatedData);
    relatedData = relatedData.concat(relatedRead.slice(0, sliceNumber));
  }

  if (relatedShop.length) {
    const sliceNumber = getSliceNumber(relatedReadCount, relatedEpisodesCount, relatedListenCount, relatedData);
    relatedData = relatedData.concat(relatedShop.slice(0, sliceNumber));
  }
  if (relatedListenCount) {
    const sliceNumber = getSliceNumber(relatedReadCount, relatedEpisodesCount, relatedShopCount, relatedData);
    relatedData = relatedData.concat(relatedListen.slice(0, sliceNumber));
  }

  const furtherListeningList = furtherListening.map((fListening) => fListening?.listening?.text).filter((e) => e);
  const furtherReadingList = furtherReading.map((fReading) => fReading?.reading?.text).filter((e) => e);
  const footNotesList = footnotes.map((elem) => elem?.footnote?.text).filter((e) => e);

  const totalRelatedContentsCount = 4;
  // check relateddata is less than 4
  if (relatedData.length < totalRelatedContentsCount) {
    const readdatas = readData.nodes;
    const allReadData = readdatas.map((item) => ({
      ...item.data,
      category: 'READ',
      url: item.url,
    }));
    const additionalDataCount = totalRelatedContentsCount - relatedData.length;
    const additionalData = allReadData.sort(() => Math.random() - 0.5).slice(0, additionalDataCount);
    relatedData = [...relatedData, ...additionalData];
  }

  //filter podcast data using podcast uid
  const podcastUid = featuredPodcast?.uid;
  const podcast = podcastUid ? podCastData?.nodes.find((pod) => pod.uid === podcastUid) : null;
  const podcastVideoUrl = podcast?.data?.videoUrl?.embed_url || '';

  const TableReadItem = read?.body1
    ?.filter((slice) => slice?.sliceType === 'text')
    .map((slice) => {
      const title = getSectionTitleFromHTML(slice?.primary?.sectionContents?.text);
      if (!title) return null;
      return {
        id: slice?.id,
        title,
        isHighlighted: false,
        isUnderlined: false,
      };
    })
    .filter(Boolean);

  return (
    <BodyClassName className="body-dark page-read page-read--single">
      <DefaultLayoutComponent seo={seoData} description={read?.seo?.metaDesc}>
        <div className="site-main site-readpage">
          <div className="wrapper wrapper-xl site-readpage">
            <Stickysidebar
              ReadItemTableContent={TableReadItem}
              featuredVideo={featuredVideo}
              featuredPodcast={podcast}
              hasAboutAuthor={Boolean(articleAuthorMember)}
            />
            <div className="read-single-main-column">
              {!!postBanner?.fluid?.src && featuredVideo?.text === '' && (
                <div className="wrapper wrapper-xl pd-x-md">
                  <div
                    className="read_thumbnail_image wrapper wrapper-xl section r_s_banner bgImage bg-light pd-x-md"
                    style={{
                      backgroundImage: `url(${postBanner?.fluid?.src})`,
                    }}
                  />
                </div>
              )}
              <ReadPageContent
                readDuration={readDuration}
                tableReadItem={TableReadItem}
                category={readCategories}
                body={read?.body1}
                featuredVideo={featuredVideo}
                postBanner={postBanner?.fluid?.src}
                featuredVideoThumbnailImage={featuredVideoThumbnail?.fluid?.src}
                // postBanner={featuredVideoThumbnail?.fluid?.src}
                featuredPodcast={podcast}
                featuredPodcastVideoUrl={podcastVideoUrl}
                title={title}
                chapterBylineAuthor={readBylineAuthorName}
                relocateAuthorGallerySliceId={relocateAuthorGallerySliceId}
              />
              {articleAuthorMember ? (
                <ReadAboutAuthor
                  teamMember={articleAuthorMember}
                  displayName={readBylineAuthorName}
                  articleGalleryUrls={readArticleGalleryUrls}
                />
              ) : null}
              <div className="wrapper wrapper-xl pd-x-md read-chapter-actions-slot">
                <div className="wrapper wrapper-md pd-x-sm">
                  <div className="wrapper-sm read-single-content-align">
                    <div className="read-chapter-actions-bar">
                      <ChapterActions
                        showShareMenu={showShareMenu}
                        setShowShareMenu={setShowShareMenu}
                        setShowReadModal={setShowReadModal}
                        showListenButton={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div id="references" className="read-footer readfooter-bg">
                <div className="wrapper wrapper-xl pd-x-md">
                  <div className={`section r_s_footer-info pd-t-0 ${
                    isListEmpty(footNotesList) && 
                    isListEmpty(furtherReadingList) &&  
                    isListEmpty(furtherListeningList) 
                      ? 'r_s_footer-info--centered' 
                      : ''
                  }`}>
                    <div className="wrapper wrapper-md pd-x-sm">
                      <div className="wrapper-sm read-single-content-align">
                        <SocialShare title={read.title} />
                      {!isListEmpty(furtherReadingList) && (
                        <div className="r_s_author">
                          <h6 className="r_s_author-name">Further Reading</h6>
                          {furtherReading.map((fReading, index) => (
                            <p key={`f-reading-${index}`} className="r_s_author-desc">
                              {fReading?.reading?.text}
                            </p>
                          ))}
                        </div>
                      )}
                      {!isListEmpty(furtherListeningList) && (
                        <div className="r_s_author">
                          <h6 className="r_s_author-name">Further Listening</h6>
                          {furtherListening.map((fListening, index) => (
                            <p key={`f-listening-${index}`} className="r_s_author-desc">
                              {fListening?.listening?.text}
                            </p>
                          ))}
                        </div>
                      )}
                      {!isListEmpty(footNotesList) && (
                        <div className="r_s_author">
                          <h6 className="r_s_author-name">Footnotes</h6>
                          {footnotes.map((fnote, index) => (
                            <p key={`footnote-${index}`} className="r_s_author-desc">
                              {fnote?.footnote?.text}
                            </p>
                          ))}
                        </div>
                      )}
                    {/* Continue Your Learning Journey Section */}
                    {(() => {
                    let nextArticle = null;
                    let prevArticle = null;
                    let nextEpisodeNumber = null;

                    const currentSeasonCategory = readCategories?.find((cat) => {
                      const uid = cat?.read_category?.uid || cat?.read_category?.slug;
                      return uid && uid.match(/season[-_]?\d+/i);
                    });

                    const currentCategory =
                      currentSeasonCategory?.read_category?.uid || currentSeasonCategory?.read_category?.slug;

                    const sameCategoryArticles = readData.nodes.filter((node) => {
                      const nodeSeasonCategory = node.data?.readCategories?.find((cat) => {
                        const uid = cat?.readCategory?.uid || cat?.readCategory?.slug;
                        return uid && uid.match(/season[-_]?\d+/i);
                      });
                      const nodeCategory =
                        nodeSeasonCategory?.readCategory?.uid || nodeSeasonCategory?.readCategory?.slug;
                      return nodeCategory === currentCategory;
                    });
                    const currentIndex = sameCategoryArticles.findIndex((node) => node.uid === currentArticleUid);

                    if (currentIndex !== -1 && sameCategoryArticles.length > 1) {
                      const nextIndex = currentIndex + 1 < sameCategoryArticles.length ? currentIndex + 1 : 0;
                      const prevIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : sameCategoryArticles.length - 1;

                      const nextNode = sameCategoryArticles[nextIndex];
                      const prevNode = sameCategoryArticles[prevIndex];

                      nextArticle = {
                        ...nextNode.data,
                        url: nextNode.url,
                      };

                      prevArticle = {
                        ...prevNode.data,
                        url: prevNode.url,
                      };

                      nextEpisodeNumber = nextIndex + 1;
                    }

                    if (!nextArticle) return null;

                    const getSeasonLabel = () => {
                      const seasonCategory = nextArticle?.readCategories?.find((cat) => {
                        const uid = cat?.readCategory?.uid || cat?.readCategory?.slug;
                        return uid && uid.match(/season[-_]?\d+/i);
                      });

                      const categorySlug =
                        seasonCategory?.readCategory?.uid || seasonCategory?.readCategory?.slug || '';
                      const seasonMatch = categorySlug.match(/season[-_]?(\d+)/i);

                      if (seasonMatch) {
                        const seasonNum = seasonMatch[1];
                        return `SEASON ${seasonNum}`;
                      }

                      return 'EPISODE';
                    };

                    return (
                      <div className="continue-journey-wrap">
                        <div className="continueJourneySection">
                          <div className="continueJourneyBanner">
                            <h4>CONTINUE YOUR LEARNING JOURNEY</h4>
                          </div>

                          <div className="nextArticlePreviewCard">
                          <button
                            className="arrowButton arrowLeft"
                            onClick={() => {
                              if (prevArticle?.url) {
                                window.location.href = prevArticle.url;
                              }
                            }}
                            disabled={!prevArticle}
                          >
                            <img src={ArrowLeft} alt="Previous" />
                          </button>

                          <div className="previewContent">
                            <div className="previewText">
                              <p className="categoryLabel">{getSeasonLabel()}</p>
                              <h3 className="articleTitlePreview">
                                {nextArticle?.title?.text || 'Next Article Title'}
                              </h3>
                            </div>

                            <div className="previewImage">
                              <img
                                src={nextArticle?.featuredImage?.fluid?.src || nextArticle?.featuredImage?.url}
                                alt={nextArticle?.title?.text || 'Next article'}
                              />
                            </div>
                          </div>

                          <button
                            className="arrowButton arrowRight"
                            onClick={() => {
                              if (nextArticle?.url) {
                                window.location.href = nextArticle.url;
                              }
                            }}
                          >
                            <img src={ArrowRight} alt="Next" />
                          </button>
                          </div>
                        </div>
                      </div>
                    );
                    })()}
                    {relatedData?.length ? (
                      <div className="read-single-related-content" style={{ marginTop: '121px' }}>
                        <RelatedContent relatedContent={relatedData} />
                      </div>
                    ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DefaultLayoutComponent>
    </BodyClassName>
  );
}

function ReadSinglePage(props) {
  const {
    data: {
      read: { nodes },
      readData,
      totalWatchData,
      podCastData,
      teamMembers,
    },
  } = props;

  // const [read, setRead] = useState(readData);

  let singlePageReadData = [];
  if (Array.isArray(nodes)) {
    singlePageReadData = nodes[0];
  }

  return (
    <ReadSingleRender
      read={singlePageReadData?.data}
      currentArticleUid={singlePageReadData?.uid}
      podCastData={podCastData}
      readData={readData}
      totalWatchData={totalWatchData}
      teamMembers={teamMembers?.nodes || []}
    />
  );
}

export const query = graphql`
  query SingleReadPageQuery($id: String!) {
    read: allPrismicRead(filter: { id: { eq: $id } }) {
      nodes {
        data {
          footnotes: footnotes1 {
            footnote {
              text
            }
          }
          title {
            text
          }
          body1 {
            ... on PrismicReadBody1Text {
              id
              primary {
                backgroundColor: background_color
                sectionContents: section_contents {
                  text: html
                }
              }
              sliceType: slice_type
            }
            ... on PrismicReadBody1Photo {
              id
              items {
                image {
                  alt
                  fluid(maxWidth: 1200) {
                    ...GatsbyPrismicImageFluid
                  }
                }
                caption {
                  html
                  raw
                  text
                }
              }
              sliceType: slice_type
              primary {
                backgroundColor: background_color
              }
            }
            ... on PrismicReadBody1Video {
              id
              primary {
                backgroundColor: background_color
                videoId: video_id {
                  text
                }
              }
              sliceType: slice_type
            }
            ... on PrismicReadBody1Quote {
              id
              primary {
                backgroundColor: background_color
                sectionContents: section_contents {
                  text
                }
              }
              sliceType: slice_type
            }
          }
          relatedEpisodes: related_episodes {
            episode {
              uid
              type
              slug
            }
          }
          relatedRead: related_reads {
            read {
              uid
              type
              slug
            }
          }
          readCategories: read_categories {
            read_category {
              uid
              type
            }
          }
          relatedListen: related_listens {
            relatedListen: related_listen {
              uid
              id
              type
            }
          }
          relatedProducts: related_products {
            product
          }

          furtherReading: further_reading1 {
            reading {
              text
            }
          }
          metaDescriptions: meta_description {
            text
          }
          featuredVideo: featured_video {
            text
          }
          bylineAuthor: byline_author
          articleAuthor: author1 {
            uid
            id
            type
            document {
              ... on PrismicTeamMember {
                data {
                  name {
                    text
                  }
                  ministryRole: ministry_role {
                    text
                  }
                  bio {
                    text
                  }
                  portraitPhoto: portrait_photo {
                    url
                  }
                  actionPhoto: action_photo {
                    url
                  }
                }
              }
            }
          }
          featuredPodcast: featured_podcast {
            uid
            slug
          }
          featuredVideoThumbnail: featured_video_thumbnail {
            fluid(maxWidth: 1400) {
              ...GatsbyPrismicImageFluid
            }
          }
          featuredImage: featured_image {
            fluid(maxWidth: 800) {
              ...GatsbyPrismicImageFluid
            }
          }
          furtherListening: further_listening1 {
            listening {
              text
            }
          }
          postBanner: post_banner1 {
            fluid(maxWidth: 1400) {
              ...GatsbyPrismicImageFluid
            }
          }

          readDuration: read_duration1 {
            raw
          }
        }
        id
        uid
        url
        last_publication_date
        first_publication_date
      }
    }
    totalWatchData: allPrismicEpisodes(sort: { fields: data___priority, order: ASC }) {
      nodes {
        data {
          title {
            text
          }
          videoDuration: video_duration {
            text
          }
          thumbnailImage: thumbnail_image {
            url
          }
          youtubeId: youtube_id {
            text
          }
        }
        uid
      }
    }
    podCastData: allPrismicPodcast(sort: { fields: first_publication_date, order: DESC }) {
      nodes {
        data {
          thumnailImage: thumbnail_image {
            url
          }
          podCastTitle: podcast_title {
            text
          }
          podcastId: podcast_id {
            text
          }
          miniPlayerThumbNail: mini_player_thumbnail {
            url
          }

          season {
            type
            uid
          }
          podcastDuration: podcast_duration
          videoUrl: video_url {
            embed_url
          }
        }
        uid
      }
    }

    teamMembers: allPrismicTeamMembers {
      nodes {
        id
        uid
        data {
          name {
            text
          }
          ministryRole: ministry_role {
            text
          }
          bio {
            text
          }
          portraitPhoto: portrait_photo {
            url
          }
          actionPhoto: action_photo {
            url
          }
        }
      }
    }

    readData: allPrismicRead(sort: { fields: data___priority }) {
      nodes {
        data {
          priority
          title {
            text
          }
          readDuration: read_duration1 {
            text
          }
          readCategories: read_categories {
            readCategory: read_category {
              uid
              slug
            }
          }
          featuredImage: featured_image {
            alt
            url
            fluid(maxWidth: 600) {
              ...GatsbyPrismicImageFluid
            }
          }
        }
        url
        uid
      }
    }
  }
`;

export default ReadSinglePage;
